import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, MessageSquare, ThumbsUp, ThumbsDown, RotateCcw, Trash2, RefreshCw, User, Globe, Lock, Filter, Languages } from "lucide-react";

const MODE_BADGE = {
  public: { icon: Globe, color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Public" },
  portal: { icon: Lock, color: "bg-amber-100 text-amber-700 border-amber-200", label: "Portail" },
};

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch { return s; }
}

function MessageRow({ msg, onFlag }) {
  const isUser = msg.role === "user";
  const flagColor = msg.flag === "bad" ? "border-rose-300 ring-2 ring-rose-200" : msg.flag === "good" ? "border-emerald-300 ring-2 ring-emerald-200" : "border-transparent";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`af-msg-${msg.role}`}>
      <div className={`max-w-[80%] rounded-2xl border-2 ${flagColor} ${isUser ? "bg-wine-50" : "bg-white"} px-4 py-3 shadow-sm`}>
        <div className="text-[10px] font-bold uppercase text-neutral-500">
          {isUser ? "👤 Utilisateur" : "🤖 Assistant"} · {fmtDate(msg.created_at)}
        </div>
        <div className="mt-1 whitespace-pre-wrap text-sm text-neutral-800">{msg.content}</div>
        {!isUser && (
          <div className="mt-2 flex items-center gap-2 border-t border-neutral-100 pt-2">
            <button
              onClick={() => onFlag(msg.id, msg.flag === "good" ? null : "good")}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${msg.flag === "good" ? "bg-emerald-600 text-white" : "border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"}`}
              data-testid={`af-flag-good-${msg.id}`}
            >
              <ThumbsUp className="h-3 w-3" /> Bonne
            </button>
            <button
              onClick={() => onFlag(msg.id, msg.flag === "bad" ? null : "bad")}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${msg.flag === "bad" ? "bg-rose-600 text-white" : "border border-rose-300 bg-white text-rose-700 hover:bg-rose-50"}`}
              data-testid={`af-flag-bad-${msg.id}`}
            >
              <ThumbsDown className="h-3 w-3" /> Mauvaise
            </button>
            {msg.flag_note && (
              <span className="text-[10px] italic text-neutral-500">&laquo;{msg.flag_note}&raquo;</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminAIFeedback() {
  const [sessions, setSessions] = useState([]);
  const [filterMode, setFilterMode] = useState("");
  const [filterFlag, setFilterFlag] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total_messages: 0, sessions: 0, bad_flags: 0, good_flags: 0 });
  const [loading, setLoading] = useState(false);
  const [loadingMsgs] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [prewarmBusy, setPrewarmBusy] = useState(false);
  const [prewarmResult, setPrewarmResult] = useState(null);

  const runDiagnose = async () => {
    setDiagBusy(true);
    setDiag(null);
    try {
      const r = await api.get("/admin/ai/diagnose");
      setDiag(r.data);
      if (r.data?.ok) toast.success("Agent IA opérationnel ✓");
      else toast.error(r.data?.error || "Diagnostic échoué");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Diagnostic impossible");
      setDiag({ ok: false, error: formatApiErrorDetail(e) || "Erreur réseau" });
    } finally {
      setDiagBusy(false);
    }
  };

  const runPrewarm = async () => {
    if (!window.confirm("Pré-traduire tout le contenu public dans les 5 langues ? Cette opération peut prendre 1–3 minutes selon le volume. Les contenus déjà traduits seront ignorés (gratuit).")) return;
    setPrewarmBusy(true);
    setPrewarmResult(null);
    try {
      const r = await api.post("/admin/translations/prewarm", {});
      setPrewarmResult(r.data);
      if (r.data?.errors > 0) toast.warning(`Pré-traduction terminée avec ${r.data.errors} erreurs`);
      else toast.success(`Pré-traduction OK — ${r.data.translations} nouvelles traductions, ${r.data.skipped} cache hits`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Pré-traduction échouée");
    } finally {
      setPrewarmBusy(false);
    }
  };

  const loadSessions = () => {
    const params = new URLSearchParams();
    if (filterMode) params.set("mode", filterMode);
    if (filterFlag) params.set("flag", filterFlag);
    api.get(`/admin/ai/sessions?${params.toString()}`).then((r) => setSessions(r.data?.items || [])).catch(() => {});
    api.get("/admin/ai/stats").then((r) => setStats(r.data || {})).catch(() => {});
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (filterMode) params.set("mode", filterMode);
    if (filterFlag) params.set("flag", filterFlag);
    api.get(`/admin/ai/sessions?${params.toString()}`).then((r) => setSessions(r.data?.items || [])).catch(() => {});
    api.get("/admin/ai/stats").then((r) => setStats(r.data || {})).catch(() => {});
  }, [filterMode, filterFlag]);

  const loadMessages = (sid) => api.get(`/admin/ai/sessions/${sid}`)
    .then((r) => setMessages(r.data?.messages || []))
    .catch((e) => toast.error(formatApiErrorDetail(e) || "Erreur"));

  const flagMsg = async (msgId, flag) => {
    let note;
    if (flag === "bad") {
      note = window.prompt("Note (optionnelle) : pourquoi cette réponse est-elle mauvaise ?") || "";
    }
    try {
      await api.patch(`/admin/ai/messages/${msgId}/flag`, { flag, note });
      setMessages((ms) => ms.map((m) => m.id === msgId ? { ...m, flag, flag_note: note || null } : m));
      loadSessions();  // refresh counts
      toast.success(flag === "bad" ? "Réponse signalée comme mauvaise" : flag === "good" ? "Réponse marquée comme bonne" : "Flag retiré");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Échec");
    }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Supprimer définitivement cette conversation ?")) return;
    try {
      await api.delete(`/admin/ai/sessions/${sid}`);
      toast.success("Conversation supprimée");
      if (selectedId === sid) { setSelectedId(""); setMessages([]); }
      loadSessions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Échec");
    }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-navy md:text-4xl">
          <Sparkles className="mr-2 inline h-7 w-7 text-amber-500" />
          AI Feedback
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={runDiagnose} disabled={diagBusy} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100 disabled:opacity-50" data-testid="af-diagnose">
            {diagBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}Diagnostiquer
          </button>
          <button onClick={runPrewarm} disabled={prewarmBusy} className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50" data-testid="af-prewarm" title="Pré-traduire tout le contenu (news, events, companies) dans les 5 langues. Idempotent : les contenus déjà traduits sont ignorés.">
            {prewarmBusy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}Pré-traduire le contenu
          </button>
          <button onClick={loadSessions} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-bold text-neutral-700 hover:bg-neutral-50" data-testid="af-refresh">
            <RefreshCw className="h-3 w-3" />Rafraîchir
          </button>
        </div>
      </div>
      <p className="mt-1 text-neutral-600">Relisez les conversations de l&apos;agent IA et signalez les mauvaises réponses pour améliorer le contexte.</p>

      {/* Prewarm result panel */}
      {prewarmResult && (
        <div className="mt-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900" data-testid="af-prewarm-panel">
          <div className="font-bold">🌐 Pré-traduction du contenu — résumé</div>
          <ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
            <li><strong>Documents scannés :</strong> {prewarmResult.docs}</li>
            <li><strong>Nouvelles traductions :</strong> {prewarmResult.translations}</li>
            <li><strong>Cache hits (gratuits) :</strong> {prewarmResult.skipped}</li>
            <li><strong>Erreurs :</strong> {prewarmResult.errors}</li>
            <li><strong>Durée :</strong> {prewarmResult.elapsed_seconds}s</li>
            <li><strong>Langues :</strong> {(prewarmResult.langs || []).join(", ")}</li>
          </ul>
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer font-semibold">Détail par collection</summary>
            <table className="mt-2 w-full text-left">
              <thead><tr className="font-semibold"><th>Collection</th><th>Docs</th><th>Traduits</th><th>Cache</th><th>Erreurs</th></tr></thead>
              <tbody>
                {Object.entries(prewarmResult.per_collection || {}).map(([col, stats]) => (
                  <tr key={col}><td>{col}</td><td>{stats.docs}</td><td>{stats.translations}</td><td>{stats.skipped}</td><td>{stats.errors}</td></tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}

      {/* Diagnostic panel */}
      {diag && (
        <div className={`mt-4 rounded-2xl border p-4 text-sm ${diag.ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-red-200 bg-red-50 text-red-900"}`} data-testid="af-diag-panel">
          <div className="font-bold">
            {diag.ok ? "✓ Agent IA opérationnel" : "✗ Agent IA indisponible"}
          </div>
          <ul className="mt-2 space-y-1 text-xs">
            <li><strong>Clé présente :</strong> {diag.key_present ? "oui" : "non"}{diag.key_masked ? ` (${diag.key_masked})` : ""}</li>
            {diag.model && <li><strong>Modèle :</strong> {diag.model}</li>}
            {diag.reply_sample && <li><strong>Réponse test :</strong> « {diag.reply_sample} »</li>}
            {diag.error_kind && <li><strong>Type d&apos;erreur :</strong> {diag.error_kind}</li>}
            {diag.error && <li><strong>Détails :</strong> <code className="break-all">{diag.error}</code></li>}
            {diag.hint && <li className="italic">{diag.hint}</li>}
          </ul>
          {!diag.ok && (
            <p className="mt-2 text-xs">
              Solutions courantes : (1) Vérifiez la variable <code>EMERGENT_LLM_KEY</code> sur Railway → Settings → Variables (production) ou dans <code>backend/.env</code> (local), puis redéployez.
              (2) Si la clé est valide mais retourne « quota », rechargez le solde via <em>Profil → Universal Key → Add Balance</em>.
            </p>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-neutral-100 bg-white p-4" data-testid="af-stat-sessions">
          <div className="text-[10px] uppercase text-neutral-500">Conversations</div>
          <div className="text-2xl font-bold text-navy">{stats.sessions || 0}</div>
        </div>
        <div className="rounded-2xl border border-neutral-100 bg-white p-4" data-testid="af-stat-messages">
          <div className="text-[10px] uppercase text-neutral-500">Messages totaux</div>
          <div className="text-2xl font-bold text-navy">{stats.total_messages || 0}</div>
        </div>
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4" data-testid="af-stat-good">
          <div className="text-[10px] uppercase text-emerald-700">👍 Bonnes</div>
          <div className="text-2xl font-bold text-emerald-700">{stats.good_flags || 0}</div>
        </div>
        <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4" data-testid="af-stat-bad">
          <div className="text-[10px] uppercase text-rose-700">👎 À corriger</div>
          <div className="text-2xl font-bold text-rose-700">{stats.bad_flags || 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-neutral-500" />
        <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs" data-testid="af-filter-mode">
          <option value="">Tous les modes</option>
          <option value="public">Public</option>
          <option value="portal">Portail</option>
        </select>
        <select value={filterFlag} onChange={(e) => setFilterFlag(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs" data-testid="af-filter-flag">
          <option value="">Tous les statuts</option>
          <option value="bad">Avec réponses mauvaises</option>
          <option value="good">Avec réponses bonnes</option>
        </select>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[400px_1fr]">
        {/* Sessions list */}
        <div className="space-y-2" data-testid="af-sessions">
          {loading && <div className="rounded-2xl border border-neutral-100 bg-white p-4 text-center text-sm text-neutral-500">Chargement…</div>}
          {!loading && sessions.length === 0 && (
            <div className="rounded-2xl border border-neutral-100 bg-white p-6 text-center text-sm text-neutral-500">
              <MessageSquare className="mx-auto h-6 w-6 text-neutral-300" />
              <div className="mt-2">Aucune conversation pour l&apos;instant.</div>
            </div>
          )}
          {sessions.map((s) => {
            const Badge = MODE_BADGE[s.mode] || MODE_BADGE.public;
            const Icon = Badge.icon;
            const isSelected = selectedId === s.session_id;
            return (
              <button
                key={s.session_id}
                onClick={() => { setSelectedId(s.session_id); loadMessages(s.session_id); }}
                className={`block w-full rounded-2xl border-2 p-3 text-left transition-all ${isSelected ? "border-wine-500 bg-wine-50" : "border-neutral-100 bg-white hover:bg-neutral-50"}`}
                data-testid={`af-session-${s.session_id}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${Badge.color}`}>
                    <Icon className="h-2.5 w-2.5" />{Badge.label}
                  </span>
                  {s.user_email ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-neutral-600">
                      <User className="h-2.5 w-2.5" />{s.user_email}
                    </span>
                  ) : (
                    <span className="text-[10px] italic text-neutral-400">anonyme</span>
                  )}
                  <span className="ml-auto text-[10px] text-neutral-400">{fmtDate(s.last_at)}</span>
                </div>
                <div className="mt-1.5 line-clamp-2 text-sm text-neutral-800">
                  {s.preview || "(pas d'aperçu)"}
                </div>
                <div className="mt-1.5 flex items-center gap-2 text-[10px] text-neutral-500">
                  <span>{s.messages_count} message(s)</span>
                  {s.good_count > 0 && <span className="rounded-full bg-emerald-100 px-1.5 text-emerald-700">👍 {s.good_count}</span>}
                  {s.bad_count > 0 && <span className="rounded-full bg-rose-100 px-1.5 text-rose-700">👎 {s.bad_count}</span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Messages panel */}
        <div className="rounded-2xl border border-neutral-100 bg-neutral-50 p-4" data-testid="af-detail">
          {!selectedId && (
            <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center text-neutral-400">
              <MessageSquare className="h-12 w-12" />
              <div className="mt-2 text-sm">Sélectionnez une conversation à gauche</div>
            </div>
          )}
          {selectedId && (
            <>
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-bold text-neutral-600">Session {selectedId.slice(0, 12)}…</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => loadMessages(selectedId)} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[10px] font-bold text-neutral-700 hover:bg-neutral-100" data-testid="af-reload-msgs">
                    <RotateCcw className="h-2.5 w-2.5" />Recharger
                  </button>
                  <button onClick={() => deleteSession(selectedId)} className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-white px-2 py-0.5 text-[10px] font-bold text-rose-700 hover:bg-rose-50" data-testid="af-delete-session">
                    <Trash2 className="h-2.5 w-2.5" />Supprimer
                  </button>
                </div>
              </div>
              {loadingMsgs ? (
                <div className="text-center text-sm text-neutral-500">Chargement…</div>
              ) : (
                <div className="space-y-2">
                  {messages.map((m) => <MessageRow key={m.id} msg={m} onFlag={flagMsg} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
